import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';
import { styled } from '@mui/material';
import { Link } from 'react-router-dom';
import { FormProvider, RHFInput, RHFPassword } from 'ui/utils/form';
import { Box, Button, Link as MuiLink, Typography } from 'ui';
import { Header } from '../../layout';
import useAuth from '../../hooks/useAuth';
import { LOGIN_ROUTES, EULA_LINK } from '../../constants';
import http from 'axios';
import { inputClasses } from '@mui/material';
import { isNewBackend } from 'config';
import palette from 'theme/palette';
import { TypographyLink } from 'pages/endpoints/components/TypographyLink';

type FormValuesProps = {
  email: string;
  password: string;
};

const getConditionalSchema = (isPasswordRequired: boolean) =>
  object().shape({
    email: string().email('Enter a valid email address').required('Enter an email address '),
    password: string().when([], {
      is: () => isPasswordRequired,
      then: string().required(),
      otherwise: string().nullable()
    })
  });

const FlexContainer = styled(Box, { shouldForwardProp: prop => prop !== 'isHidden' })<{ isHidden?: boolean }>(
  ({ theme, isHidden }) => ({
    marginTop: '6px',
    display: isHidden ? 'none' : 'flex',
    flexDirection: 'column',
    gap: '16px'
  })
);

const LinkStyled = styled(Link)(() => ({
  textDecoration: 'none'
}));

const StyledInput = styled(RHFInput)<{ shouldShowPasswordField: boolean }>(({ theme, shouldShowPasswordField }) => {
  return {
    minHeight: shouldShowPasswordField ? '' : '62px',
    [`& .${inputClasses.disabled} fieldset`]: {
      border: `1px solid ${theme.palette.grey[300]} !important`
    },
    [`& .${inputClasses.disabled} input`]: {
      color: theme.palette.text.disabled,
      WebkitTextFillColor: theme.palette.grey[300]
    },
    [`& .${inputClasses.disabled} svg path`]: {
      fill: theme.palette.grey[300]
    },
    '& input:-webkit-autofill': {
      WebkitBoxShadow: `0 0 0 100px ${theme.palette.background.default} inset`,
      borderRadius: theme.shape.borderRadius
    }
  };
});

const Main = styled(Box)<{ shouldShowPasswordField: boolean }>(({ theme, shouldShowPasswordField }) => ({
  width: theme.spacing(52),
  display: 'flex',
  flexDirection: 'column',
  gap: shouldShowPasswordField ? '16px' : 0
}));

const LoginWithSSOForm = () => {
  const [shouldShowPasswordField, setShouldShowPasswordField] = useState(false);
  const { login, loading, error, isLoginOk } = useAuth();

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(getConditionalSchema(shouldShowPasswordField)),
    mode: 'onSubmit',
    defaultValues: {
      email: '',
      password: ''
    }
  });

  const { handleSubmit, reset } = methods;

  const onSubmit = async ({ email, password }: FormValuesProps) => {
    try {
      // add password field to the payload only when condition is met
      await login({
        email,
        ...(password !== '' && shouldShowPasswordField && { password })
      });
    } catch (err: unknown) {
      if (http.isAxiosError(err) && err.response?.status === 401) {
        setShouldShowPasswordField(true);
      }

      if (!shouldShowPasswordField && !isNewBackend) {
        setShouldShowPasswordField(true);
      }
    }
  };

  return (
    <FormProvider fullHeight onSubmit={handleSubmit(onSubmit)} methods={methods} autoComplete={true}>
      <Main style={{ marginTop: '38vh' }} shouldShowPasswordField={shouldShowPasswordField}>
        <Header title="Sign in" />

        <StyledInput
          name="email"
          type="email"
          placeholder="Email address"
          autoComplete="email"
          autoFocus={true}
          dataTestId="login-email"
          size="small"
          disabled={shouldShowPasswordField}
          shouldShowPasswordField={shouldShowPasswordField}
        />
        {shouldShowPasswordField && (
          <Button
            variant="text"
            size="large"
            sx={{ justifyContent: 'flex-start', padding: 0 }}
            onClick={() => {
              setShouldShowPasswordField(false);
              reset();
            }}
            color="inherit"
          >
            <Typography color={palette.primary.link} variant="subtitle2">
              {'< Sign in with another user'}
            </Typography>
          </Button>
        )}

        <RHFPassword name="password" style={shouldShowPasswordField ? {} : { display: 'none' }} />
        <FlexContainer>
          <Button type="submit" formNoValidate loading={loading || isLoginOk} dataTestId="login-submit">
            Login
          </Button>

          <Typography display="flex" justifyContent="start" variant="body2">
            By clicking Login, you agree to Cynet's{' '}
            <MuiLink
              variant="body2"
              ml="3px"
              underline="hover"
              target="_blank"
              href={EULA_LINK}
              color={palette.primary.link}
            >
              Terms & Conditions
            </MuiLink>
          </Typography>
          {shouldShowPasswordField && (
            <LinkStyled to={LOGIN_ROUTES.FORGOT_PASSWORD}>
              <TypographyLink
                hasLink
                display="flex"
                justifyContent="center"
                mt={1}
                color={palette.primary.link}
                variant="subtitle2"
              >
                Forgot password?
              </TypographyLink>
            </LinkStyled>
          )}
        </FlexContainer>

        {error && (
          <Typography color="error" variant="subtitle2" sx={{ margin: 'auto' }}>
            {error}
          </Typography>
        )}
      </Main>
    </FormProvider>
  );
};

export default LoginWithSSOForm;
